import { useEffect, useRef, useState } from 'react';
import { Loading } from '@appkit4/react-components';
import styles from './ThinkingIndicator.module.scss';

interface ThinkingIndicatorProps {
    statusMessage?: string | null;
}

const ThinkingIndicator = ({ statusMessage }: ThinkingIndicatorProps) => {
    const [dots, setDots] = useState('');
    const [displayedStatus, setDisplayedStatus] = useState<string | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setDots((prev) => {
                if (prev.length >= 3) return '';
                return prev + '.';
            });
        }, 500);

        return () => clearInterval(interval);
    }, []);

    // Crossfade when statusMessage changes
    useEffect(() => {
        if (statusMessage !== displayedStatus) {
            // Start fade-out
            setIsTransitioning(true);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // After fade-out, swap text and fade-in
            timeoutRef.current = setTimeout(() => {
                setDisplayedStatus(statusMessage ?? null);
                setIsTransitioning(false);
            }, 200);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [statusMessage]); // eslint-disable-line react-hooks/exhaustive-deps

    const text = displayedStatus || `Thinking${dots}`;

    return (
        <div className={styles.thinkingMessage}>
            <div className={styles.content}>
                <div>
                    <div className="ask-appkit-avatar"></div>
                </div>
                <div>
                    <div className={styles.wrapper}>
                        <div className={styles.thinkingContent}>
                            <div className={styles.loadingWrapper}>
                                <Loading
                                    indeterminate
                                    loadingType="circular"
                                    style={{
                                        transform: 'scale(0.67)',
                                        display: 'inline-block',
                                    }}
                                />
                            </div>
                            <span
                                className={`${styles.thinkingText} ${isTransitioning ? styles.fadeOut : styles.fadeIn}`}>
                                {text}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThinkingIndicator;
